// src/pages/PainelChamados/CamposFormulario.jsx
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL; // CRA usa prefixo REACT_APP_

export default function CamposFormulario({
  tipoServico,
  setTipoServico,
  finalizadoEm,
  setFinalizadoEm,
}) {
  const hoje = new Date();

  // 🔹 Data e hora no formato que o MySQL entende
  const dataHoje = hoje.toISOString().slice(0, 10); // YYYY-MM-DD
  const horaAgora = hoje.toTimeString().slice(0, 8); // HH:MM:SS

  const [dataAbertura, setDataAbertura] = useState(dataHoje);
  const [horaAbertura, setHoraAbertura] = useState(horaAgora);
  const [opcoesServico, setOpcoesServico] = useState([]);
  const [openServico, setOpenServico] = useState(false);

  const refServico = useRef(null);

  // 🔹 Carrega lista da API + ORDENA A-Z
  useEffect(() => {
    const carregarServicos = async () => {
      try {
        const resp = await fetch(`${API_URL}/tipo-servico/listar.php`);
        if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
        const json = await resp.json();

        if (json && Array.isArray(json.dados)) {
          // 🔹 Ordena as opções alfabeticamente
          const ordenados = json.dados.sort((a, b) =>
            a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
          );

          setOpcoesServico(ordenados);
        } else {
          setOpcoesServico([]);
        }
      } catch (error) {
        console.error("Erro ao carregar serviços:", error);
        setOpcoesServico([]);
      }
    };
    carregarServicos();
  }, []);

  // 🔹 Fecha dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (refServico.current && !refServico.current.contains(e.target)) {
        setOpenServico(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // 🔹 Filtro conforme digita
  const opcoesFiltradas = opcoesServico.filter((s) =>
    s.nome.toLowerCase().includes(tipoServico.toLowerCase())
  );

  const inputBaseClasses = `
    peer w-full h-10 px-2 border-b-[1px] border-gray-300
    focus:border-blue-600 focus:ring-0 focus:outline-none
    text-base text-gray-900 bg-transparent placeholder-transparent
  `;

  const labelBaseClasses = (value) => `
    absolute left-2 transition-all duration-200 pointer-events-none
    ${
      value
        ? "-top-4 text-sm font-bold text-gray-900"
        : "top-2 text-base text-gray-500"
    }
    peer-focus:-top-4 peer-focus:text-sm peer-focus:font-bold peer-focus:text-gray-900
  `;

  return (
    <div className="p-6 flex flex-wrap gap-6 items-end">
      {/* Data de Abertura */}
      <div className="relative w-44">
        <input
          type="date"
          value={dataAbertura}
          onChange={(e) => setDataAbertura(e.target.value)}
          placeholder=" "
          className={inputBaseClasses}
        />
        <label className={labelBaseClasses(dataAbertura)}>
          Data de Abertura
        </label>
      </div>

      {/* Hora de Abertura */}
      <div className="relative w-32">
        <input
          type="time"
          step="1"
          value={horaAbertura}
          onChange={(e) => setHoraAbertura(e.target.value)}
          placeholder=" "
          className={inputBaseClasses}
        />
        <label className={labelBaseClasses(horaAbertura)}>Hora</label>
      </div>

      {/* Data de Finalização */}
      <div className="relative w-44">
        <input
          type="date"
          value={finalizadoEm}
          onChange={(e) => setFinalizadoEm(e.target.value)}
          placeholder=" "
          className={inputBaseClasses}
        />
        <label className={labelBaseClasses(finalizadoEm)}>
          Data de Finalização
        </label>
      </div>

      {/* Tipo de Serviço */}
      <div className="relative flex-1 min-w-[250px]" ref={refServico}>
        <input
          type="text"
          value={tipoServico}
          onChange={(e) => {
            setTipoServico(e.target.value);
            setOpenServico(true);
          }}
          onFocus={() => setOpenServico(true)}
          placeholder=" "
          className={inputBaseClasses + " cursor-text"}
        />
        <label className={labelBaseClasses(tipoServico)}>
          Tipo de Serviço
        </label>

        <ChevronDown
          size={16}
          onClick={() => setOpenServico((p) => !p)}
          className={`absolute right-1 top-3 cursor-pointer transition-transform ${
            openServico ? "rotate-180" : "rotate-0"
          }`}
        />

        <AnimatePresence>
          {openServico && opcoesFiltradas.length > 0 && (
            <motion.ul
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 
                         rounded-md shadow-md z-10 max-h-52 overflow-y-auto"
            >
              {opcoesFiltradas.map((s) => (
                <li
                  key={s.id}
                  onMouseDown={() => {
                    setTipoServico(s.nome);
                    setOpenServico(false);
                  }}
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-100 cursor-pointer"
                >
                  {s.nome}
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
